import { useAddProductsToWishlistMutation, type WishlistItemInput } from "@pco/api/generated/graphql";
import { useCustomerData } from "@pco/utils/useCustomerData";
import { useCallback } from "react";

/**
 * Hook to sync wishlist items in the background.
 * 
 * This hook provides a function to add multiple products
 * to the user's wishlist in a single mutation call.
 * 
 * It is intended to be used for background syncing, for example,
 * syncing guest wishlist items stored in sessionStorage
 * to the user's wishlist upon login.
 */
export const useWishlistSync = () => {

    const { data: customerData } = useCustomerData();
    const wishlistId = customerData?.customer?.wishlists?.[0]?.id;

    const {
        mutateAsync: addProductsToWishlistMutation,
        isPending: isAddingProducts,
        isError: isAddingProductsError,
    } = useAddProductsToWishlistMutation();

    const addProductsToWishlist = useCallback(
        async (items: WishlistItemInput[]) => {
            if (!wishlistId) {
                throw new Error("Wishlist ID is required");
            }
            return addProductsToWishlistMutation({
                wishlistId,
                wishlistItems: items,
            });
        },
        [wishlistId, addProductsToWishlistMutation]
    );

    return {
        addProductsToWishlist,
        isAddingProducts,
        isAddingProductsError,
    };
};